import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import Button from "@/common/elements/Button";
import Table from "@/common/elements/Table";
import { StyledFlex, StyledGridCol, StyledGridRow } from "@/common/styles";
import { PlusOutlined } from "@ant-design/icons";
import FolderDetailSearchModal from "@/components/FolderDetailSearch/FolderDetailSearchModal";
import InmateAssociatesModal from "./InmateAssociatesModal";
import {
  getOptionsFromRefList,
  hasCheckSecurity,
} from "@/util/common_function";
import API_ENDPOINTS from "@/util/api"; // adjust the path if needed
import notificationService from "@/util/notificationService";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";
import { useVocabHeaders } from "@/hooks/useVocab";
import { Popconfirm } from "antd";

// Phone headings are PCI's defaults; a site's Vocabulary rows rename them.
const ASSOCIATES_HEADERS = [
  { key: "fullName", text: "Full Name" },
  { key: "displayAssociateType", text: "Associate Type" },
  { key: "phone", text: "Evening Phone" },
  { key: "daytimePhone", text: "Daytime Phone" },
  { key: "relationship", text: "Relationship" },
  { key: "comments", text: "Comments" },
];

const AssociatesDetails = (startingPoint = "") => {
  const dispatch = useDispatch();
  const associateFormRef = useRef();
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state.bookings
  );

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md).
  const {
    headers: associatesHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("Bookings:BookingsJacketsJackets", ASSOCIATES_HEADERS);
  const shownAssociatesHeaders = useVocabHeaders(associatesHeaders);
  const [associateseModal, setAssociateseModal] = useState(false);
  const [folderSearchModal, setFolderSearchModal] = useState(false);
  // This state now holds the outer array from bookings.bookingsJacketsJackets
  const [associateData, setAssociateData] = useState([]);
  // selectedAssociate is an outer associate object.
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  const isEnabled = bookingsFieldsEnabled;

  // On mount and when bookings change, iterate over bookings.bookingsJacketsJackets.
  useEffect(() => {
    const outerArray = bookings?.bookingsJacketsJackets || [];
    // Ensure each outer object has a uuid.
    const updatedArray = outerArray.map((item) => ({
      ...item,
      uuid: item.uuid || uuidv4(),
    }));
    setAssociateData(updatedArray);
  }, [bookings?.bookingsJacketsJackets]);

  const handleAssociateseModal = () => {
    setAssociateseModal(!associateseModal);
    setIsEdit(false);
  };

  // When user clicks Add, clear selection and open modal.
  const handleAddAssociates = () => {
    setSelectedAssociate(null);
    handleAssociateseModal();
    setTimeout(() => {
      setAssociateseModal(true);
      setFolderSearchModal(true);
    }, 500);
  };

  // Get a label for the associate type.
  const getLabelFromID = (id) => {
    const value = getOptionsFromRefList(
      "Inmate_Associate_Type",
      false,
      false,
      true
    ).filter((item) => item.value === id)?.[0]?.label;
    return value;
  };

  const editAssociate = () => {
    setIsEdit(true);
    setAssociateseModal(!associateseModal);
  };

  // When a folder is selected in FolderDetailSearchModal, build an associate object.
  // Also fires for that modal's "Create New Folder" button (same onSaveClick
  // callback) — meta.isNewJacket tells the two apart.
  const handleSelectCurrentJacket = (selectedFolder, meta) => {
    if (!selectedFolder) {
      alert("Please select a folder from the list first.");
      return;
    }
    const folderData = selectedFolder?.originalData;
    const associateFromFolder = {
      uuid: folderData?.associatedWithJacketID || uuidv4(),
      associateTypeID: folderData?.associateType,
      // Nest the folder data under jackets as required.
      jackets: folderData?.jackets
        ? [folderData?.jackets]
        : [
            {
              staffJacket: false,
              orphanFlag: false,
              bio: [selectedFolder],
            },
          ],
      // Distinguishes this from onTableRowClick's "edit an existing associate"
      // path — only a fresh EXISTING jacket pick should require field
      // verification. A brand-new folder (meta.isNewJacket) has nothing to
      // verify against.
      isFreshJacketPick: !meta?.isNewJacket,
    };

    // Store the entire outer associate object instead of just the inner element.
    setSelectedAssociate(associateFromFolder);
    setFolderSearchModal(false);
  };

  // When a table row is clicked, select that outer associate.
  const onTableRowClick = (rowData) => {
    if (!rowData.uuid) {
      rowData.uuid = uuidv4();
    }
    setSelectedAssociate({ ...rowData });
  };

  // Delete the selected associate and call the delete API URL.
  const handleDeleteAssociate = () => {
    if (!selectedAssociate) return;

    // Remove the associate from the local state
    const updatedInnerArray = associateData.filter(
      (item) => item.uuid !== selectedAssociate.uuid
    );
    const payload = { bookingsJacketsJackets: updatedInnerArray };
    dispatch(setBookings(payload));
    setAssociateData(updatedInnerArray);
    setSelectedAssociate(null);

    const url =
      API_ENDPOINTS.associates +
      "id/" +
      selectedAssociate.bookingsJacketsJacketID;
    dispatch(setDeleteAPis(url));
  };

  // updateStoreObj updates the outer array in Redux.
  const updateStoreObj = () => {
    associateFormRef?.current?.form
      ?.validateFields()
      .then((formData) => {
        if (associateFormRef?.current?.hasUnverifiedFields?.()) {
          notificationService.error(
            "Please verify all fields copied from the selected record before saving."
          );
          return;
        }
        let updatedAssociate;
        const existingAssociate = associateData?.find(
          (item) => item?.uuid === selectedAssociate?.uuid
        );
        if (isEdit && selectedAssociate) {
          // Merge form data into existing outer associate.
          const existingBio =
            (selectedAssociate?.jackets &&
              selectedAssociate?.jackets[0] &&
              selectedAssociate?.jackets[0]?.bio &&
              selectedAssociate?.jackets[0]?.bio[0]) ||
            {};

          const mergedBio = {
            ...existingBio,
            lName: formData?.lName,
            fName: formData?.fName,
            mName: formData?.mName,
            suffix: formData?.suffix,
            daytimePhone: formData?.daytimePhone,
            phone: formData?.phone,
            bioAliases: formData?.bioAliases || [],
            bioAddresses: formData?.bioAddresses || [],
          };
          const mergedJacket = {
            ...(selectedAssociate?.jackets
              ? selectedAssociate?.jackets[0]
              : {}),
            jacketId: selectedAssociate?.jackets?.[0]?.jacketId,
            bio: [mergedBio],
          };
          updatedAssociate = {
            bookingsJacketsJacketID: existingAssociate?.bookingsJacketsJacketID,
            bookingID: existingAssociate?.bookingID || bookings.bookingId,
            jacketsJacketID: existingAssociate?.jacketsJacketID,
            // Always update only the 0th element.
            jacketsJackets: [
              {
                ...selectedAssociate,
                associateTypeID: formData?.associateTypeID,
                relationship: formData?.relationship,
                comments: formData?.comments,
                jackets: [mergedJacket],
              },
            ],
          };
        } else {
          updatedAssociate = {
            bookingsJacketsJacketID: existingAssociate?.bookingsJacketsJacketID,
            bookingID: existingAssociate?.bookingID || bookings.bookingId,
            jacketsJacketID: existingAssociate?.jacketsJacketID,
            uuid: uuidv4(),
            jacketsJackets: [
              {
                associateTypeID: formData?.associateTypeID,
                relationship: formData?.relationship,
                comments: formData?.comments,
                jackets: [
                  {
                    ...(selectedAssociate?.jackets
                      ? selectedAssociate?.jackets[0]
                      : {}),
                    jacketId: selectedAssociate?.jackets?.[0]?.jacketId,
                    bio: [
                      {
                        lName: formData?.lName,
                        fName: formData?.fName,
                        mName: formData?.mName,
                        suffix: formData?.suffix,
                        daytimePhone: formData?.daytimePhone,
                        phone: formData?.phone,
                        bioAliases: formData?.bioAliases || [],
                        bioAddresses: formData?.bioAddresses || [],
                      },
                    ],
                  },
                ],
              },
            ],
          };
        }
        let updatedArray = [];
        if (isEdit && selectedAssociate) {
          updatedArray = associateData?.map((item) =>
            item.uuid === selectedAssociate?.uuid ? updatedAssociate : item
          );
        } else {
          updatedArray = [...associateData, updatedAssociate];
        }
        const payload = { bookingsJacketsJackets: updatedArray };
        dispatch(setBookings(payload));
        handleAssociateseModal();
      })
      .catch((err) => console.error(err));
  };

  return (
    <>
      <StyledGridRow justify="end">
        <StyledGridCol style={{ paddingBottom: "10px" }}>
          <StyledFlex align="center" gap="10px">
            <Button
              onClick={handleAddAssociates}
              disabled={
                !isEnabled ||
                isConfiguring ||
                !hasCheckSecurity(
                  Security_Types.functionality,
                  Entity_types.subModule,
                  LABELS_CAPITAL.ADD,
                  MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                  SUB_MODULES_LIST_WITH_ID.Arrest_Report
                )
              }
            >
              <span>
                <PlusOutlined />
              </span>{" "}
              Add
            </Button>
            <Button
              onClick={editAssociate}
              disabled={
                !selectedAssociate ||
                isConfiguring ||
                !hasCheckSecurity(
                  Security_Types.functionality,
                  Entity_types.subModule,
                  LABELS_CAPITAL.VIEW,
                  MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                  SUB_MODULES_LIST_WITH_ID.Arrest_Report
                )
              }
            >
              Details
            </Button>
            <Button
              onClick={handleDeleteAssociate}
              disabled={
                !isEnabled ||
                !selectedAssociate ||
                isConfiguring ||
                !hasCheckSecurity(
                  Security_Types.functionality,
                  Entity_types.subModule,
                  LABELS_CAPITAL.DELETE,
                  MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                  SUB_MODULES_LIST_WITH_ID.Arrest_Report
                )
              }
            >
              Delete
            </Button>

            {/* Config — drag-reorder/resize columns; only while Modify is active */}
            {!isConfiguring ? (
              <Button
                htmlType="button"
                onClick={startConfiguring}
              >
                {configButtonLabel}
              </Button>
            ) : (
              <Popconfirm
                title="Do you want to save the current list view configuration?"
                onConfirm={confirmSave}
                onCancel={discardChanges}
                okText="Yes"
                cancelText="No"
              >
                <Button htmlType="button">
                  {configButtonLabel}
                </Button>
              </Popconfirm>
            )}
          </StyledFlex>
        </StyledGridCol>
      </StyledGridRow>

      {hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.VIEW,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report
      ) && (
        <Table
          ref={dataTableRef}
          headers={shownAssociatesHeaders}
          draggable={isConfiguring}
          onRowClick={onTableRowClick}
          onRowDoubleClick={(data) => {
            onTableRowClick(data);
            editAssociate();
          }}
          data={associateData.map((item) => ({
            ...item,
            ...item.jacketsJackets?.[0],
            uuid: item.uuid,
            // Do not spread the inner object to avoid flattening the outer data.
            fullName: `${
              item.jacketsJackets?.[0]?.jackets?.[0]?.bio?.[0]?.fName || ""
            } ${
              item.jacketsJackets?.[0]?.jackets?.[0]?.bio?.[0]?.mName || ""
            } ${
              item.jacketsJackets?.[0]?.jackets?.[0]?.bio?.[0]?.lName || ""
            }`.trim(),
            displayAssociateType: getOptionsFromRefList(
              "Inmate_Associate_Type",
              false,
              false,
              true
            ).filter(
              (opt) => opt.value === item.jacketsJackets?.[0].associateTypeID
            )?.[0]?.label,
            phone: item.jacketsJackets?.[0]?.jackets?.[0]?.bio?.[0]?.phone,
            daytimePhone:
              item.jacketsJackets?.[0]?.jackets?.[0]?.bio?.[0]?.daytimePhone,
          }))}
        />
      )}
      {associateseModal && (
        <InmateAssociatesModal
          show={associateseModal}
          associateFormRef={associateFormRef}
          selectedAssociate={selectedAssociate}
          onHide={handleAssociateseModal}
          updateStoreObj={updateStoreObj}
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          startingPoint={startingPoint}
        />
      )}

      {folderSearchModal && (
        <FolderDetailSearchModal
          show={folderSearchModal}
          onHide={() => setFolderSearchModal(false)}
          onSaveClick={handleSelectCurrentJacket}
        />
      )}
    </>
  );
};

export default AssociatesDetails;
